import type { ActiveContext } from '@/types/active-context'
import type { UserRole } from '@/types/account'

export interface DefaultContextOptions {
  role?: UserRole | null
  accountType?: string | null
}

/** Pick the persisted context when it still exists in the available set. */
export function resolvePersistedContext(
  contexts: ActiveContext[],
  persisted: ActiveContext | null,
): ActiveContext | null {
  if (!persisted) return null
  return contexts.find((context) => context.id === persisted.id) ?? null
}

/**
 * Default active context on login — always personal.
 * Org workspace data lives in availableContexts; role-based routing gates /org/*.
 */
export function selectDefaultActiveContext(
  contexts: ActiveContext[],
  _opts?: DefaultContextOptions,
): ActiveContext {
  const personal = contexts.find((context) => context.scope === 'personal')
  return personal ?? contexts[0]
}

export function pickActiveContext(
  contexts: ActiveContext[],
  opts?: DefaultContextOptions & { persisted?: ActiveContext | null },
): ActiveContext {
  const persisted = resolvePersistedContext(contexts, opts?.persisted ?? null)
  // Honor any persisted context that still exists in available set.
  // Admins can switch via ContextSwitcher; regular users stay on personal.
  if (persisted) return persisted

  return selectDefaultActiveContext(contexts, opts)
}

export function hasOrganizationContext(contexts: ActiveContext[]): boolean {
  return contexts.some((context) => context.scope === 'organization')
}
